import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useDashboardSearchCondition } from '@/providers/DashboardSearchConditionProvider';
import { MotionDiv } from '@/components/element/MotionDiv';
import { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  CoreChartOptions,
  ElementChartOptions,
  PluginChartOptions,
  DatasetChartOptions,
  ScaleChartOptions,
  DoughnutControllerChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { PurchaseDashboardAmountByTeam, usePurchaseDashboardAmountByTeam } from '@/hooks/api/2170_purchase_dashboard_amount_by_team';

ChartJS.register(ArcElement, Tooltip, Legend);

const colors = ['bg-gradient-primary', 'bg-gradient-secondary', 'bg-gradient-info', 'bg-gradient-success', 'bg-gradient-warning'];

const options: _DeepPartialObject<
  CoreChartOptions<'doughnut'> &
    ElementChartOptions<'doughnut'> &
    PluginChartOptions<'doughnut'> &
    DatasetChartOptions<'doughnut'> &
    ScaleChartOptions<'doughnut'> &
    DoughnutControllerChartOptions
> = {
  cutout: '70',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  scales: {
    y: {
      border: {
        display: false,
      },
      grid: {
        display: false,
        drawOnChartArea: false,
        drawTicks: false,
      },
      ticks: {
        display: false,
      },
    },
    x: {
      border: {
        display: false,
      },
      grid: {
        display: false,
        drawOnChartArea: false,
        drawTicks: false,
      },
      ticks: {
        display: false,
      },
    },
  },
};

const createBgColors = (ctx: CanvasRenderingContext2D) => {
  const g1 = ctx.createLinearGradient(0, 0, 0, 100);
  g1.addColorStop(0, '#825ee4');
  g1.addColorStop(1, '#1b46c2');
  const g2 = ctx.createLinearGradient(0, 0, 0, 100);
  g2.addColorStop(0, '#A8B8D8');
  g2.addColorStop(1, '#627594');
  const g3 = ctx.createLinearGradient(0, 0, 0, 100);
  g3.addColorStop(0, '#21D4FD');
  g3.addColorStop(1, '#2152FF');
  const g4 = ctx.createLinearGradient(0, 0, 0, 100);
  g4.addColorStop(0, '#98EC2D');
  g4.addColorStop(1, '#17AD37');
  const g5 = ctx.createLinearGradient(0, 0, 0, 100);
  g5.addColorStop(0, '#FBCF33');
  g5.addColorStop(1, '#F53939');

  return [g1, g2, g3, g4, g5];
};

export const AmountByTeam = () => {
  const { _w_ } = useCurrentWorkspace();
  const { purchaseOrdersDashboardSearchCondition: searchCondition, setPurchaseOrdersDashboardSearchCondition: setSearchCondition } =
    useDashboardSearchCondition();
  const { data, isPending } = usePurchaseDashboardAmountByTeam({ ...searchCondition, identifier: _w_!.identifier });

  if (!data || data.names.length == 0) return <></>;

  return (
    <MotionDiv className="col-12 mb-4 col-lg-12">
      <div className="card">
        <div className="card-header pb-0 p-3">
          <h6 className="mb-0">チーム別案件合計金額</h6>
        </div>
        <div className="card-body p-3">
          <div className="d-flex align-items-start">
            <div className="text-center me-3" style={{ width: 160 }}>
              <div className="doughnut-chart">
                <AmountByTeamChart d={data} />
                <h6 className="font-weight-bold">
                  <span>{data.amounts.reduce((a, b) => a + b, 0).toLocaleString()}</span>
                  <span className="d-block text-body text-sm font-weight-bolder text-gradient text-secondary">YEN</span>
                </h6>
              </div>
            </div>
            <div className="flex-1" style={{ minWidth: 0 }}>
              <div className="table-responsive">
                <table className="table align-items-center mb-0">
                  <tbody>
                    {data.names.map((d, i) => (
                      <tr key={`t-name-${i}`}>
                        <td>
                          <div className="d-flex px-2 py-0">
                            <span className={`badge me-2 ${colors[i]}`}> </span>
                            <h6 className="mb-0 text-xs">{d}</h6>
                          </div>
                        </td>
                        <td className="align-middle text-end text-xs font-weight-bold">¥{data.amounts[i].toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MotionDiv>
  );
};

const AmountByTeamChart = ({ d }: { d: PurchaseDashboardAmountByTeam }) => {
  const [chartData, setChartData] = useState<ChartData<'doughnut'>>({ labels: [], datasets: [] });
  const chartRef = useRef<ChartJSOrUndefined<'doughnut'>>(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    setChartData({
      labels: d.names,
      datasets: [
        {
          weight: 9,
          borderWidth: 1,
          backgroundColor: createBgColors(chart.ctx),
          data: d.amounts,
        },
      ],
    });
  }, [d]);

  return <Doughnut data={chartData} ref={chartRef} options={options} style={{ height: 160, width: 160 }} />;
};
